import React from 'react';

/**
 * Shared PDF table used inside PdfA4Page.
 *
 * Column config (shared with Excel export):
 *  { key, label, align: 'center' | 'start', width?: '20%', render?: (row, globalIndex) => node }
 *  - '#' column is added automatically with CONTINUOUS numbering.
 *  - All cells are horizontally centered and vertically middle,
 *    except columns marked align: 'start' (long text).
 */
const borderedCell = {
  border: '1px solid #000',
  padding: '6px',
  verticalAlign: 'middle',
};

export const pdfCellValue = (row, col, globalIndex) => {
  if (col.render) return col.render(row, globalIndex);
  const value = row?.[col.key];
  return value === null || value === undefined || value === ''
    ? '—'
    : String(value);
};

const PdfTable = ({
  columns,
  rows,
  startIndex = 0,
  isRTL = true,
  fontSize = '11px',
  headless = false,
  renderRow, // optional fully-custom row renderer (row, globalIndex, localIndex) => <tr/>
}) => {
  const headerCell = {
    ...borderedCell,
    textAlign: 'center',
    fontWeight: '700',
    backgroundColor: 'rgba(238, 242, 247, 0.7)',
  };

  const bodyCell = (col, idx) => ({
    ...borderedCell,
    textAlign: col.align === 'start' ? (isRTL ? 'right' : 'left') : 'center',
    direction: isRTL ? 'rtl' : 'ltr',
    wordBreak: 'break-word',
    backgroundColor:
      idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)',
  });

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <table
        style={{
          width: '100%',
          // Fixed layout: identical column widths on every page AND in the
          // measurement template — required for accurate page-fill calculation.
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
          fontSize,
          border: '1px solid #000',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
      >
        {!headless && (
          <thead>
            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
              <th style={{ ...headerCell, width: '5%' }}>#</th>
              {columns.map(col => (
                <th
                  key={col.key || col.label}
                  style={{ ...headerCell, width: col.width }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, idx) => {
            const globalIndex = startIndex + idx;
            if (renderRow) return renderRow(row, globalIndex, idx);
            return (
              <tr
                key={row?.id ?? globalIndex}
                style={{
                  backgroundColor:
                    idx % 2 === 0
                      ? 'rgba(255, 255, 255, 0.7)'
                      : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <td style={bodyCell({ align: 'center' }, idx)}>
                  {globalIndex + 1}
                </td>
                {columns.map(col => (
                  <td key={col.key || col.label} style={bodyCell(col, idx)}>
                    {pdfCellValue(row, col, globalIndex)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PdfTable;
